import {AccountingService} from "@/accounting/services/AccountingService";
import {CustomTableService} from "@/services/CustomTableService";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {removePaginationFromURLParams} from "@/utils";
import {dateToFec, displayDate} from "@/accounting/services/export/AccountingFormat";
import {downloadWorkbook, singleTableWorkbook} from "@/accounting/services/export/ExcelTable";

// 01/08/2026 -> 01-08-2026, pour un nom de fichier
const fileDate = date => displayDate(dateToFec(date)).replace(/\//g, '-');

/**
 * Comportement commun des tableaux de l'espace comptabilité (encaissements, achats, décaissements) : le professionnel consulté,
 * la période sélectionnée, le chargement des lignes et de leur résumé, les filtres rapides « parmi » (boutons) et l'export Excel.
 *
 * Le composant fournit :
 *  - un CustomTable de référence `table`, et les données `data`, `resumeData` et `searchOptions` (dont le filtre de période) ;
 *  - `dateField` : le champ date sur lequel porte la période, qui est aussi celui de l'ordre chronologique de l'export ;
 *  - `requestTable(urlParams, professionalId)` et `requestResume(urlParams, professionalId)` : les appels à l'API.
 */
export const AccountingTableMixin = {
    computed: {
        isAccountantModule() {
            return localStorage && localStorage.options && localStorage.options.includes('accountant');
        },
        periodData() {
            return AccountingService.data;
        },
        // Comptable : le professionnel sélectionné (undefined pour un professionnel sur ses propres données)
        professionalId() {
            return this.isAccountantModule ? AccountingService.data.selectedProfessional?.id : undefined;
        },
    },
    watch: {
        async "periodData.selectedPeriod"(period) {
            AccountingService.updatePeriodFilters(this.searchOptions.filters, this.dateField, period);
            await this.reload();
        },
    },
    async mounted() {
        this.$refs.table.$refs.searchArray.initWithURLParams();
        await this.reload();
    },
    methods: {
        async reload() {
            this.$refs.table?.startLoading();
            await this.loadData();
            await this.loadResumeData();
            this.$refs.table?.stopLoading();
        },
        // Lignes correspondant à la recherche (période + filtres), avec ou sans la pagination du tableau
        async fetchRows(urlParams) {
            // Comptable : aucun professionnel sélectionné, rien à charger
            if (this.isAccountantModule && !this.professionalId) return [];
            const resp = await this.requestTable(urlParams, this.professionalId);
            return resp && !resp.errors ? resp : [];
        },
        async loadData() {
            if (!this.$refs.table) return;
            this.data = [];
            this.data = await this.fetchRows(this.$refs.table.buildURLParams());
        },
        async loadResumeData() {
            if (this.isAccountantModule && !this.professionalId) return;
            if (!this.$refs.table) return;
            this.resumeData = undefined;
            const resp = await this.requestResume(removePaginationFromURLParams(this.$refs.table.buildURLParams()), this.professionalId);
            if (resp && !resp.errors) {
                this.resumeData = resp;
            }
        },
        // Export de toute la recherche (période + filtres), sans la pagination du tableau, dans l'ordre chronologique
        async downloadExcel(title, columns) {
            const rows = await this.fetchRows(removePaginationFromURLParams(this.$refs.table.buildURLParams()));
            rows.sort((a, b) => new Date(a[this.dateField]) - new Date(b[this.dateField]));
            const {StartPeriodDate, EndPeriodDate} = this.periodData.selectedPeriod;
            downloadWorkbook(singleTableWorkbook(title, columns, rows), `${title} du ${fileDate(StartPeriodDate)} au ${fileDate(EndPeriodDate)}.xlsx`);
        },
        currentFilters() {
            return CustomTableService.data.get(this.$refs.table?.tableId)?.currentFilters ?? [];
        },
        // Filtre « parmi » (mode, motif, statut...) : plusieurs valeurs possibles, le filtre disparaît quand il n'en reste aucune
        isFilterActive(field, value) {
            return this.currentFilters().some(filter => filter.field === field && filter.operator === FILTER_OPERATOR_ENUM.IN.value && filter.variable.includes(value));
        },
        async toggleFilter(field, value) {
            const filters = this.currentFilters();
            const filter = filters.find(current => current.field === field && current.operator === FILTER_OPERATOR_ENUM.IN.value);
            if (!filter) {
                filters.push({field, operator: FILTER_OPERATOR_ENUM.IN.value, variable: [value]});
            } else {
                filter.variable = filter.variable.includes(value) ? filter.variable.filter(current => current !== value) : [...filter.variable, value];
                if (filter.variable.length === 0) filters.splice(filters.indexOf(filter), 1);
            }
            await this.reload();
        },
    },
};
